import { useState } from 'react';

import { currentTheme } from '@/shared/lib/theme';
import { useTranslation } from 'react-i18next';

import {
  cardBackdrops,
  cardLookSource,
  type Look,
  NEUTRAL_SURFACE,
  STANDARD_LOOK,
} from '@/entities/appearance';
import { type Column, useSetCardLook } from '@/entities/board';
import type { CardDetail } from '@/entities/card';
import {
  isLookValid,
  LookEditor,
  LookPreview,
  lookPayload,
  TextColors,
  withStyle,
  withTextColor,
} from '@/features/appearance-editor';
import { FormError } from '@/shared/forms';
import { Button, Dialog } from '@/shared/ui';

import styles from './CoverEditor.module.css';

const CARD_STYLES = ['header', 'border', 'accent', 'soft', 'solid', 'gradient', 'pattern'] as const;

interface Props {
  card: CardDetail;
  /** The card's current column (its default card look is what "inherit" means). */
  column: Column | undefined;
  onClose: () => void;
}

/**
 * The card's own look. "Same as the column" (the default) follows the column's default card
 * look, also after the card is moved; "Own look" overrides it and travels with the card.
 */
export function CardAppearanceDialog({ card, column, onClose }: Props) {
  const { t } = useTranslation();
  const setLook = useSetCardLook();
  // A card created before look snapshots still follows its column; every other card owns a copy
  // of the look it was created with (restyling the column never changes it).
  const legacy = card.appearance === null;
  const [own, setOwn] = useState(!legacy);
  const [look, setDraft] = useState<Look | null>(card.appearance);
  const [error, setError] = useState<unknown>(null);
  const shown: Look | null = own ? lookPayload(look) : (column?.card_look ?? null);
  const source = cardLookSource({ appearance: own ? look : null }, column ?? { card_look: null });
  const valid = !own || isLookValid(look);
  const neutral = NEUTRAL_SURFACE[currentTheme() === 'dark' ? 'dark' : 'light'];
  const backdrops = cardBackdrops(look, neutral);

  return (
    <Dialog title={t('cardLook.title')} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          // Saved as an explicit look (the standard one included): the card keeps it.
          setLook.mutate(
            { cardId: card.id, look: own ? (lookPayload(look) ?? STANDARD_LOOK) : null },
            { onSuccess: onClose, onError: setError },
          );
        }}
      >
        <LookPreview look={shown} />
        {legacy && (
          <div className={styles.kinds} role="radiogroup" aria-label={t('cardLook.mode')}>
            <button
              type="button"
              role="radio"
              aria-checked={!own}
              className={styles.kind}
              data-testid="card-look-inherit"
              onClick={() => {
                setOwn(false);
              }}
            >
              {t('cardLook.inherit')}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={own}
              className={styles.kind}
              data-testid="card-look-own"
              onClick={() => {
                setOwn(true);
                setDraft((current) => current ?? withStyle(STANDARD_LOOK, 'soft'));
              }}
            >
              {t('cardLook.own')}
            </button>
          </div>
        )}
        {!own && (
          <p className={styles.hint}>
            {source === 'column' ? t('cardLook.inheritFromColumn') : t('cardLook.inheritStandard')}
          </p>
        )}
        {own && (
          <>
            {!legacy && <p className={styles.hint}>{t('cardLook.snapshotHint')}</p>}
            <LookEditor value={look} onChange={setDraft} allowed={CARD_STYLES} idPrefix="card" />
            <TextColors
              idPrefix="card"
              fields={[
                { key: 'title_color', label: t('appearance.text.fields.title'), role: 'text' },
                { key: 'text_color', label: t('appearance.text.fields.text'), role: 'text' },
                { key: 'muted_color', label: t('appearance.text.fields.muted'), role: 'text' },
                { key: 'surface', label: t('appearance.text.fields.surface'), role: 'surface' },
              ]}
              values={{
                title_color: look?.title_color,
                text_color: look?.text_color,
                muted_color: look?.muted_color,
                surface: look?.surface,
              }}
              onChange={(key, value) => {
                setDraft((current) =>
                  withTextColor(
                    current,
                    key as 'title_color' | 'text_color' | 'muted_color' | 'surface',
                    value,
                  ),
                );
              }}
              backdrops={() => backdrops}
            />
            {!legacy && column && (
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(column.card_look);
                }}
              >
                {t('cardLook.copyColumn')}
              </Button>
            )}
          </>
        )}
        <p className={styles.hint}>{t('cardLook.statesHint')}</p>
        <FormError error={error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={setLook.isPending || !valid}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
